import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { Toaster as HotToaster } from "react-hot-toast";
import "@/App.css";

import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import TablePaginationEnhancer from "@/components/common/TablePaginationEnhancer";
import { PermissionsProvider, usePermissions } from "@/hooks/usePermissions";
import { PersonaProvider } from "@/hooks/usePersona";
import { JourneyProvider } from "@/hooks/useJourney";
import PersonaRoute from "@/components/PersonaRoute";
import { canUseBusinessIntelligence } from "@/lib/featureAccess";

// Lazy-load every page so the initial JS bundle only includes Login + Layout.
// Each page becomes its own chunk that downloads on first navigation.
// A page's code is a hashed chunk, and a deploy used to delete the previous
// build's chunks: a tab opened before the deploy then asked for a file that
// was gone and the page "404ed" (72 such requests at the load balancer,
// 21–23 Sep 2026, the CFO dashboard among them). One reload fetches the new
// index.html and its chunks; the flag stops a loop if the reload cannot help.
const lazyWithReload = (factory) => lazy(() => factory().then((mod) => {
  try { sessionStorage.removeItem("chunk-reload-once"); } catch (e) { /* storage blocked: nothing to clear */ }
  return mod;
}).catch((err) => {
  let reloaded = false;
  try { reloaded = !!sessionStorage.getItem("chunk-reload-once"); } catch (e) { /* storage blocked: reload once anyway */ }
  if (!reloaded) {
    try { sessionStorage.setItem("chunk-reload-once", "1"); } catch (e) { /* ignore */ }
    window.location.reload();
    return new Promise(() => {});   // the reload takes over
  }
  throw err;
}));
const Dashboard = lazyWithReload(() => import("@/pages/Dashboard"));
const Clients = lazyWithReload(() => import("@/pages/Clients"));
const ClientCreate = lazyWithReload(() => import("@/pages/ClientCreate"));
const ClientDetails = lazyWithReload(() => import("@/pages/ClientDetails"));
const Projects = lazyWithReload(() => import("@/pages/Projects"));
// Van Expense — parallel additive connected workspace (UI-only). New pages that
// consume existing APIs; existing routes/pages are untouched.
const VanDashboard = lazyWithReload(() => import("@/pages/van/VanDashboard"));
const VanFind = lazyWithReload(() => import("@/pages/van/VanFind"));
const VanProjectDetail = lazyWithReload(() => import("@/pages/van/VanProjectDetail"));
const VanCampaignDetail = lazyWithReload(() => import("@/pages/van/VanCampaignDetail"));
const VanLocationDetail = lazyWithReload(() => import("@/pages/van/VanLocationDetail"));
const VanVehicleDetail = lazyWithReload(() => import("@/pages/van/VanVehicleDetail"));
const VanDriverDetail = lazyWithReload(() => import("@/pages/van/VanDriverDetail"));
const VanDailyExpenses = lazyWithReload(() => import("@/pages/van/VanDailyExpenses"));
const VanPayments = lazyWithReload(() => import("@/pages/van/VanPayments"));
const ProjectDetails = lazyWithReload(() => import("@/pages/ProjectDetails"));
const ProjectClosure = lazyWithReload(() => import("@/pages/ProjectClosure"));
const Campaigns = lazyWithReload(() => import("@/pages/Campaigns"));
const GodownManagerView = lazyWithReload(() => import("@/pages/GodownManagerView"));
const CampaignDetails = lazyWithReload(() => import("@/pages/CampaignDetails"));
const CampaignDriverDashboard = lazyWithReload(() => import("@/pages/CampaignDriverDashboard"));
const CampaignCreate = lazyWithReload(() => import("@/pages/CampaignCreate"));
const CampaignSetup = lazyWithReload(() => import("@/pages/CampaignSetup"));
const ProjectCreate = lazyWithReload(() => import("@/pages/ProjectCreate"));
const VendorCreate = lazyWithReload(() => import("@/pages/VendorCreate"));
const VendorDetails = lazyWithReload(() => import("@/pages/VendorDetails"));
const VehicleCreate = lazyWithReload(() => import("@/pages/VehicleCreate"));
const VehicleDetails = lazyWithReload(() => import("@/pages/VehicleDetails"));
const DriverCreate = lazyWithReload(() => import("@/pages/DriverCreate"));
const DriverDetails = lazyWithReload(() => import("@/pages/DriverDetails"));
const PromoterCreate = lazyWithReload(() => import("@/pages/PromoterCreate"));
const PromoterDetails = lazyWithReload(() => import("@/pages/PromoterDetails"));
const AnchorCreate = lazyWithReload(() => import("@/pages/AnchorCreate"));
const AnchorDetails = lazyWithReload(() => import("@/pages/AnchorDetails"));
const DailyReporting = lazyWithReload(() => import("@/pages/DailyReporting"));
const VoiceAgent = lazyWithReload(() => import("@/pages/VoiceAgent"));
const PromoterActivityForm = lazyWithReload(() => import("@/pages/PromoterActivityForm"));
const PromoterActivityDetails = lazyWithReload(() => import("@/pages/PromoterActivityDetails"));
const ExpenseCreate = lazyWithReload(() => import("@/pages/ExpenseCreate"));
const ExpenseCreateChooser = lazyWithReload(() => import("@/pages/ExpenseCreateChooser"));
const ExpenseCreateAccounts = lazyWithReload(() => import("@/pages/ExpenseCreateAccounts"));
const ExpensesBulkUpload = lazyWithReload(() => import("@/pages/ExpensesBulkUpload"));
const BulkVendorPayment = lazyWithReload(() => import("@/pages/BulkVendorPayment"));
const ExpenseReconciliation = lazyWithReload(() => import("@/pages/ExpenseReconciliation"));
const Vendors = lazyWithReload(() => import("@/pages/Vendors"));
const Vehicles = lazyWithReload(() => import("@/pages/Vehicles"));
const Drivers = lazyWithReload(() => import("@/pages/Drivers"));
const Promoters = lazyWithReload(() => import("@/pages/Promoters"));
const Anchors = lazyWithReload(() => import("@/pages/Anchors"));
const Operations = lazyWithReload(() => import("@/pages/Operations"));
const Expenses = lazyWithReload(() => import("@/pages/Expenses"));
const ExpenseDetails = lazyWithReload(() => import("@/pages/ExpenseDetails"));
const Reports = lazyWithReload(() => import("@/pages/Reports"));
const ReportCreate = lazyWithReload(() => import("@/pages/ReportCreate"));
const ReportDetails = lazyWithReload(() => import("@/pages/ReportDetails"));
const ReportTemplates = lazyWithReload(() => import("@/pages/ReportTemplates"));
const RetainerDashboard = lazyWithReload(() => import("@/pages/RetainerDashboard"));
const FLPnL = lazyWithReload(() => import("@/pages/FLPnL"));
const AccessDenied = lazyWithReload(() => import("@/pages/AccessDenied"));
const VendorDashboard = lazyWithReload(() => import("@/pages/VendorDashboard"));
const ClientServicingDashboard = lazyWithReload(() => import("@/pages/ClientServicingDashboard"));
const DriverDashboard = lazyWithReload(() => import("@/pages/DriverDashboard"));
const JourneyDetails = lazyWithReload(() => import("@/pages/JourneyDetails"));
const InvoiceUpload = lazyWithReload(() => import("@/pages/InvoiceUpload"));
const InvoiceDetails = lazyWithReload(() => import("@/pages/InvoiceDetails"));
const PaymentDetails = lazyWithReload(() => import("@/pages/PaymentDetails"));
const PaymentRequests = lazyWithReload(() => import("@/pages/PaymentRequests"));
const Accounts = lazyWithReload(() => import("@/pages/Accounts"));
const AdvanceReconciliation = lazyWithReload(() => import("@/pages/AdvanceReconciliation"));
const PaymentsTracker = lazyWithReload(() => import("@/pages/PaymentsTracker"));
const PaymentAllocations = lazyWithReload(() => import("@/pages/PaymentAllocations"));
const Overheads = lazyWithReload(() => import("@/pages/Overheads"));
const AccountDashboard = lazyWithReload(() => import("@/pages/AccountDashboard"));
const Analytics = lazyWithReload(() => import("@/pages/Analytics"));
const WelcomePage = lazyWithReload(() => import("@/pages/WelcomePage"));
const Settings = lazyWithReload(() => import("@/pages/Settings"));
const UserManagement = lazyWithReload(() => import("@/pages/UserManagement"));
const UserRegistration = lazyWithReload(() => import("@/pages/UserRegistration"));
const RolesPermissions = lazyWithReload(() => import("@/pages/RolesPermissions"));
const MasterData = lazyWithReload(() => import("@/pages/MasterData"));
const MasterDataManagement = lazyWithReload(() => import("@/pages/MasterDataManagement"));
const SystemConfiguration = lazyWithReload(() => import("@/pages/SystemConfiguration"));
const Integrations = lazyWithReload(() => import("@/pages/Integrations"));
const AuditLog = lazyWithReload(() => import("@/pages/AuditLog"));
const HolidaysAdmin = lazyWithReload(() => import("@/pages/HolidaysAdmin"));
const TallyVoucherMappings = lazyWithReload(() => import("@/pages/TallyVoucherMappings"));
const Godowns = lazyWithReload(() => import("@/pages/Godowns"));
const GodownInventory = lazyWithReload(() => import("@/pages/GodownInventory"));
const DamagedItems = lazyWithReload(() => import("@/pages/DamagedItems"));
const DriverApproval = lazyWithReload(() => import("@/pages/DriverApproval"));
const CampaignMembers = lazyWithReload(() => import("@/pages/CampaignMembers"));
const ForbiddenPage = lazyWithReload(() => import("@/pages/ForbiddenPage"));
const CampaignInventoryDashboard = lazyWithReload(() => import("@/pages/CampaignInventoryDashboard"));
const OperationsMemberDashboard = lazyWithReload(() => import("@/pages/OperationsMemberDashboard"));
const WarehouseInventory = lazyWithReload(() => import("@/pages/WarehouseInventory"));
const WarehouseInventoryTest = lazyWithReload(() => import("@/pages/WarehouseInventoryTest"));
const CampaignInventory = lazyWithReload(() => import("@/pages/CampaignInventory"));
const CampaignInventoryManager = lazyWithReload(() => import("@/pages/CampaignInventoryManager"));
const GodownItemCreate = lazyWithReload(() => import("@/pages/GodownItemCreate"));
const CampaignReportDashboard = lazyWithReload(() => import("@/pages/CampaignReportDashboard"));
// Orders (FL000XXX cost centres — CRM-converted leads)
const Orders = lazyWithReload(() => import("@/pages/Orders"));
const ClientPOs = lazyWithReload(() => import("@/pages/ClientPOs"));
const OrderApprovals = lazyWithReload(() => import("@/pages/OrderApprovals"));
const OrderReconciliation = lazyWithReload(() => import("@/pages/OrderReconciliation"));
const AccountsBillingCorrectionsPage = lazyWithReload(() => import("@/pages/OrderReconciliation").then((m) => ({ default: m.AccountsBillingCorrectionsPage })));
const EstimateTemplateApprovals = lazyWithReload(() => import("@/pages/EstimateTemplateApprovals"));

// Invoices — Tally Sales/Purchase voucher hub (also doubles as Tally import).
const Invoices = lazyWithReload(() => import("@/pages/Invoices"));
// CRM document invoices (crm_invoices) — consolidated list, like the Orders (FL) list.
const CRMInvoices = lazyWithReload(() => import("@/pages/CRMInvoices"));

// Purchase Module
const PurchaseRequests = lazyWithReload(() => import("@/pages/PurchaseRequests"));
const PurchaseRequestForm = lazyWithReload(() => import("@/pages/PurchaseRequestForm"));
const PurchaseRequestDetails = lazyWithReload(() => import("@/pages/PurchaseRequestDetails"));
const RFQs = lazyWithReload(() => import("@/pages/RFQs"));
const RFQDetails = lazyWithReload(() => import("@/pages/RFQDetails"));
const PurchaseOrders = lazyWithReload(() => import("@/pages/PurchaseOrders"));
const PurchaseOrderDetails = lazyWithReload(() => import("@/pages/PurchaseOrderDetails"));
const POPaymentRequests = lazyWithReload(() => import("@/pages/POPaymentRequests"));
const OFMRequests = lazyWithReload(() => import("@/pages/OFMRequests"));
const OFMRequestDetails = lazyWithReload(() => import("@/pages/OFMRequestDetails"));

// Task Management
const Tasks = lazyWithReload(() => import("@/pages/Tasks"));
const TaskDetails = lazyWithReload(() => import("@/pages/TaskDetails"));
const ChatPage = lazyWithReload(() => import("@/pages/Chat"));
const Meetings = lazyWithReload(() => import("@/pages/Meetings"));

// CRM (Flowlu-mirrored Opportunities/Leads kanban)
const CRM = lazyWithReload(() => import("@/pages/CRM"));
const CRMLeadDetail = lazyWithReload(() => import("@/pages/CRMLeadDetail"));
const CRMAccounts = lazyWithReload(() => import("@/pages/CRMAccounts"));
const CRMActivities = lazyWithReload(() => import("@/pages/CRMActivities"));
const CRMAutomation = lazyWithReload(() => import("@/pages/CRMAutomation"));
const CRMArchived = lazyWithReload(() => import("@/pages/CRMArchived"));
const TargetAccounts = lazyWithReload(() => import("@/pages/TargetAccounts"));

// Analytics — revenue/cost dashboards
const AnalyticsProfitability = lazyWithReload(() => import("@/pages/AnalyticsProfitability"));
const AnalyticsPipeline = lazyWithReload(() => import("@/pages/AnalyticsPipeline"));
const BusinessIntelligence = lazyWithReload(() => import("@/pages/BusinessIntelligence"));

// My account (profile + support tickets + feature requests)
const MyAccount = lazyWithReload(() => import("@/pages/MyAccount"));
const ChangePassword = lazyWithReload(() => import("@/pages/ChangePassword"));
const SupportDashboard = lazyWithReload(() => import("@/pages/SupportDashboard"));
const SupportTicketDetail = lazyWithReload(() => import("@/pages/SupportTicketDetail"));
const SupportFeatureDetail = lazyWithReload(() => import("@/pages/SupportFeatureDetail"));

// Employee directory + xlsx importer (Admin/HR)
const EmployeeImport = lazyWithReload(() => import("@/pages/EmployeeImport"));
const EmployeeDirectory = lazyWithReload(() => import("@/pages/EmployeeDirectory"));
const EmployeeDetail = lazyWithReload(() => import("@/pages/EmployeeDetail"));

const OperationsManagerDashboard = lazyWithReload(() => import("@/pages/OperationsManagerDashboard"));
const PurchaseDashboard = lazyWithReload(() => import("@/pages/PurchaseDashboard"));
const ExpenseApprovals = lazyWithReload(() => import("@/pages/ExpenseApprovals"));
const CreativeDashboard = lazyWithReload(() => import("@/pages/CreativeDashboard"));
const KnowledgeBase = lazyWithReload(() => import("@/pages/KnowledgeBase"));
const HelpManual = lazyWithReload(() => import("@/pages/HelpManual"));
const RelationshipMap = lazyWithReload(() => import("@/pages/RelationshipMap"));

// CEO Command Centre — only visible to CEO / MD / Leadership / COO roles.
const CXODashboard = lazyWithReload(() => import("@/pages/CXODashboard"));
const CFODashboard = lazyWithReload(() => import("@/pages/CFODashboard"));
const MDDashboard = lazyWithReload(() => import("@/pages/MDDashboard"));

// BD persona dashboards — picked at runtime by /personas/me. Each route is
// gated by PersonaRoute so an unauthorised persona gets a 403 instead of
// rendering a dashboard that doesn't match their employee-master row.
const BDExecutiveDashboard = lazyWithReload(() => import("@/pages/personas/BDExecutiveDashboard"));
const BTLClientServicingDashboard = lazyWithReload(() => import("@/pages/personas/BTLClientServicingDashboard"));
const DigitalClientServicingDashboard = lazyWithReload(() => import("@/pages/personas/DigitalClientServicingDashboard"));
const PureBDDashboard = lazyWithReload(() => import("@/pages/personas/PureBDDashboard"));
const LeadGenDashboard = lazyWithReload(() => import("@/pages/personas/LeadGenDashboard"));

const RouteFallback = () => (
  <div className="flex items-center justify-center h-96">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  return token ? children : <Navigate to="/login" replace />;
};

const BusinessIntelligenceRoute = ({ children }) => {
  let user = {};
  try {
    user = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    user = {};
  }
  return canUseBusinessIntelligence(user) ? children : <Navigate to="/403" replace />;
};

// OFM operations page — Finance Control (Chitra) only. Superadmin keeps the
// standard break-glass view. Backend endpoints enforce the same independently.
const FinanceControlRoute = ({ children }) => {
  const { systemRole, role, isSuperadmin, loading } = usePermissions();
  if (loading) {
    return <div className="flex items-center justify-center h-screen bg-background" />;
  }
  const r = (systemRole || role || '').toLowerCase();
  return (isSuperadmin || r === 'finance_control') ? children : <Navigate to="/403" replace />;
};

// Renders GodownManagerView for godown_manager role; Campaigns for everyone else.
// Must be a component (not inline JSX) so it can call usePermissions() which
// requires being rendered inside PermissionsProvider.
const GodownManagerSwitch = () => {
  const { systemRole } = usePermissions();
  if (systemRole === 'godown_manager') return <GodownManagerView />;
  return (
    <ProtectedRoute requiredMenuKey="campaigns" requiredMenuAction="read">
      <Campaigns />
    </ProtectedRoute>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <PermissionsProvider>
        <PersonaProvider>
        <BrowserRouter>
          <JourneyProvider>
          <TablePaginationEnhancer />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/403" element={<AccessDenied />} />

              {/* Protected routes inside Layout */}
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <Layout />
                  </PrivateRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="account-dashboard" element={
                  <ProtectedRoute requiredMenuKey="account-dashboard" requiredMenuAction="view">
                    <AccountDashboard />
                  </ProtectedRoute>
                } />
            
                {/* Per-entity detail pages live under their canonical CRUD routes
                    (`/clients/:id`, `/projects/:id`, `/campaigns/:id`, etc.).
                    The previous "interconnected" alias dashboards have been
                    removed — there is now exactly one detail page per entity. */}

                {/* Cross-entity dashboards (not duplicates — these aggregate
                    across many campaigns, not one). */}
                <Route path="campaign-inventory" element={
                  <ProtectedRoute requiredMenuKey="campaign-inventory" requiredMenuAction="read">
                    <CampaignInventoryDashboard />
                  </ProtectedRoute>
                } />
                <Route path="operations-members" element={
                  <ProtectedRoute requiredMenuKey="campaigns" requiredMenuAction="read">
                    <OperationsMemberDashboard />
                  </ProtectedRoute>
                } />
                <Route path="relationship-map" element={
                  <ProtectedRoute requiredMenuKeys={['crm', 'projects', 'campaigns', 'clients']} requiredMenuAction="read">
                    <RelationshipMap />
                  </ProtectedRoute>
                } />
                <Route path="relationship-map/:entityType/:entityId" element={
                  <ProtectedRoute requiredMenuKeys={['crm', 'projects', 'campaigns', 'clients']} requiredMenuAction="read">
                    <RelationshipMap />
                  </ProtectedRoute>
                } />
                <Route path="business-intelligence" element={
                  <BusinessIntelligenceRoute>
                    <BusinessIntelligence />
                  </BusinessIntelligenceRoute>
                } />

                {/* ── Van Expense — parallel additive connected workspace (UI-only).
                    New entry points over EXISTING functionality. Each route gated
                    by the existing menu key of its domain — no new permission.
                    Existing routes above are unchanged. ── */}
                <Route path="van-expense" element={<ProtectedRoute requiredMenuKey="projects" requiredMenuAction="read"><VanDashboard /></ProtectedRoute>} />
                <Route path="van-expense/find" element={<ProtectedRoute requiredMenuKey="projects" requiredMenuAction="read"><VanFind /></ProtectedRoute>} />
                <Route path="van-expense/drivers/:id" element={<ProtectedRoute requiredMenuKey="drivers" requiredMenuAction="read"><VanDriverDetail /></ProtectedRoute>} />
                <Route path="van-expense/vehicles/:id" element={<ProtectedRoute requiredMenuKey="vehicles" requiredMenuAction="read"><VanVehicleDetail /></ProtectedRoute>} />
                <Route path="van-expense/locations/:id" element={<ProtectedRoute requiredMenuKey="campaigns" requiredMenuAction="read"><VanLocationDetail /></ProtectedRoute>} />
                <Route path="van-expense/campaigns/:id" element={<ProtectedRoute requiredMenuKey="campaigns" requiredMenuAction="read"><VanCampaignDetail /></ProtectedRoute>} />
                <Route path="van-expense/projects/:id" element={<ProtectedRoute requiredMenuKey="projects" requiredMenuAction="read"><VanProjectDetail /></ProtectedRoute>} />
                <Route path="van-expense/expenses" element={<ProtectedRoute requiredMenuKey="promoter-activities" requiredMenuAction="read"><VanDailyExpenses /></ProtectedRoute>} />
                <Route path="van-expense/money" element={<ProtectedRoute requiredMenuKey="finance" requiredMenuAction="read"><VanPayments /></ProtectedRoute>} />

                {/* Legacy Dashboard Routes */}
            
                {/* CEO Command Centre — role-guarded inside the component itself */}
                <Route path="cxo-dashboard" element={<CXODashboard />} />
                <Route path="cfo-dashboard" element={<CFODashboard />} />
                <Route path="md-dashboard" element={<MDDashboard />} />

                {/* Vendor Dashboard - for vendor role */}
                <Route path="vendor-dashboard" element={<VendorDashboard />} />
            
                {/* Client Servicing Dashboard - for client_servicing and admin roles */}
                <Route path="client-servicing-dashboard" element={<ClientServicingDashboard />} />
                {/* Campaign Driver Dashboard - detailed campaign view for operations/admin */}
                <Route path="campaign-driver-dashboard" element={<CampaignDriverDashboard />} />
            
                {/* Driver Dashboard - for driver, operations_manager and admin roles */}
                <Route path="driver-dashboard" element={<DriverDashboard />} />

                {/* Journey Details - for driver, operations_manager and admin roles */}
                <Route path="journey/:driverId/:date" element={<JourneyDetails />} />
                <Route path="journey/me/:date" element={<JourneyDetails />} />
            
                <Route path="invoices/new" element={
                  <ProtectedRoute requiredMenuKey="finance" requiredMenuAction="create">
                    <InvoiceUpload />
                  </ProtectedRoute>
                } />
                <Route path="invoices/:id" element={
                  <ProtectedRoute requiredMenuKey="finance" requiredMenuAction="read">
                    <InvoiceDetails />
                  </ProtectedRoute>
                } />
                <Route path="payments/:id" element={
                  <ProtectedRoute requiredMenuKey="finance" requiredMenuAction="read">
                    <PaymentDetails />
                  </ProtectedRoute>
                } />
            
                {/* Clients - require client.read permission */}
                <Route path="clients" element={
                  <ProtectedRoute requiredMenuKey="clients" requiredMenuAction="read">
                    <Clients />
                  </ProtectedRoute>
                } />
                <Route path="clients/new" element={
                  <ProtectedRoute requiredMenuKey="clients" requiredMenuAction="create">
                    <ClientCreate />
                  </ProtectedRoute>
                } />
                <Route path="clients/:id" element={
                  <ProtectedRoute requiredMenuKey="clients" requiredMenuAction="read">
                    <ClientDetails />
                  </ProtectedRoute>
                } />
                <Route path="clients/:id/edit" element={
                  <ProtectedRoute requiredMenuKey="clients" requiredMenuAction="update">
                    <ClientCreate />
                  </ProtectedRoute>
                } />
            
                {/* Projects - require project.read permission */}
                <Route path="projects" element={
                  <ProtectedRoute requiredMenuKey="projects" requiredMenuAction="read">
                    <Projects />
                  </ProtectedRoute>
                } />
                {/* Project Closure board — every internal user can view; approve/reject is
                    enforced per department by the backend. Auth-only (no menu key). */}
                <Route path="project-closure" element={
                  <ProtectedRoute>
                    <ProjectClosure />
                  </ProtectedRoute>
                } />
                <Route path="projects/new" element={
                  <ProtectedRoute requiredMenuKey="projects" requiredMenuAction="create">
                    <ProjectCreate />
                  </ProtectedRoute>
                } />
                <Route path="projects/:id" element={
                  <ProtectedRoute requiredMenuKey="projects" requiredMenuAction="read">
                    <ProjectDetails />
                  </ProtectedRoute>
                } />
                <Route path="projects/:id/edit" element={
                  <ProtectedRoute requiredMenuKey="projects" requiredMenuAction="update">
                    <ProjectCreate />
                  </ProtectedRoute>
                } />
            
                {/* Campaigns - require campaign.read permission */}
                <Route path="campaigns" element={<GodownManagerSwitch />} />
                <Route path="campaigns/new" element={
                  <ProtectedRoute requiredMenuKey="campaigns" requiredMenuAction="create">
                    <CampaignCreate />
                  </ProtectedRoute>
                } />
                <Route path="campaigns/:id" element={
                  <ProtectedRoute requiredMenuKey="campaigns" requiredMenuAction="read">
                    <CampaignDetails />
                  </ProtectedRoute>
                } />
                <Route path="campaigns/:id/edit" element={
                  <ProtectedRoute requiredMenuKey="campaigns" requiredMenuAction="update">
                    <CampaignCreate />
                  </ProtectedRoute>
                } />
                <Route path="campaigns/:id/setup" element={
                  <ProtectedRoute requiredMenuKey="campaigns" requiredMenuAction="update">
                    <CampaignSetup />
                  </ProtectedRoute>
                } />
            
                {/* Vendors */}
                <Route path="vendors" element={
                  <ProtectedRoute requiredMenuKey="vendors" requiredMenuAction="read">
                    <Vendors />
                  </ProtectedRoute>
                } />
                <Route path="vendors/new" element={
                  <ProtectedRoute requiredMenuKey="vendors" requiredMenuAction="create">
                    <VendorCreate />
                  </ProtectedRoute>
                } />
                <Route path="vendors/:id" element={
                  <ProtectedRoute requiredMenuKey="vendors" requiredMenuAction="read">
                    <VendorDetails />
                  </ProtectedRoute>
                } />
                <Route path="vendors/:id/edit" element={
                  <ProtectedRoute requiredMenuKey="vendors" requiredMenuAction="update">
                    <VendorCreate />
                  </ProtectedRoute>
                } />
            
                {/* Vehicles */}
                <Route path="vehicles" element={
                  <ProtectedRoute requiredMenuKey="vehicles" requiredMenuAction="read">
                    <Vehicles />
                  </ProtectedRoute>
                } />
                <Route path="vehicles/new" element={
                  <ProtectedRoute requiredMenuKey="vehicles" requiredMenuAction="create">
                    <VehicleCreate />
                  </ProtectedRoute>
                } />
                <Route path="vehicles/:id" element={
                  <ProtectedRoute requiredMenuKey="vehicles" requiredMenuAction="read">
                    <VehicleDetails />
                  </ProtectedRoute>
                } />
                <Route path="vehicles/:id/edit" element={
                  <ProtectedRoute requiredMenuKey="vehicles" requiredMenuAction="update">
                    <VehicleCreate />
                  </ProtectedRoute>
                } />
            
                {/* Drivers */}
                <Route path="drivers" element={
                  <ProtectedRoute requiredMenuKey="drivers" requiredMenuAction="read">
                    <Drivers />
                  </ProtectedRoute>
                } />
                <Route path="drivers/new" element={
                  <ProtectedRoute requiredMenuKey="drivers" requiredMenuAction="create">
                    <DriverCreate />
                  </ProtectedRoute>
                } />
                <Route path="drivers/:id" element={
                  <ProtectedRoute requiredMenuKey="drivers" requiredMenuAction="read">
                    <DriverDetails />
                  </ProtectedRoute>
                } />
                <Route path="drivers/:id/edit" element={
                  <ProtectedRoute requiredMenuKey="drivers" requiredMenuAction="update">
                    <DriverCreate />
                  </ProtectedRoute>
                } />
            
                {/* Promoters */}
                <Route path="promoters" element={
                  <ProtectedRoute requiredMenuKey="promoters" requiredMenuAction="read">
                    <Promoters />
                  </ProtectedRoute>
                } />
                <Route path="promoters/new" element={
                  <ProtectedRoute requiredMenuKey="promoters" requiredMenuAction="create">
                    <PromoterCreate />
                  </ProtectedRoute>
                } />
                <Route path="promoters/:id" element={
                  <ProtectedRoute requiredMenuKey="promoters" requiredMenuAction="read">
                    <PromoterDetails />
                  </ProtectedRoute>
                } />
                <Route path="promoters/:id/edit" element={
                  <ProtectedRoute requiredMenuKey="promoters" requiredMenuAction="update">
                    <PromoterCreate />
                  </ProtectedRoute>
                } />
                <Route path="anchors" element={
                  <ProtectedRoute requiredMenuKey="anchors" requiredMenuAction="read">
                    <Anchors />
                  </ProtectedRoute>
                } />
                <Route path="anchors/new" element={
                  <ProtectedRoute requiredMenuKey="anchors" requiredMenuAction="create">
                    <AnchorCreate />
                  </ProtectedRoute>
                } />
                <Route path="anchors/:id" element={
                  <ProtectedRoute requiredMenuKey="anchors" requiredMenuAction="read">
                    <AnchorDetails />
                  </ProtectedRoute>
                } />
                <Route path="anchors/:id/edit" element={
                  <ProtectedRoute requiredMenuKey="anchors" requiredMenuAction="update">
                    <AnchorCreate />
                  </ProtectedRoute>
                } />
                {/* Daily Reporting — dual-mode workspace. Executives (admin /
                    ops manager / client servicing / reporting executive) get
                    the cascade picker and fill on behalf of others. Drivers,
                    promoters and anchors get a self-fill view that auto-loads
                    their own assignments for the day. Replaces the old
                    "Promoter Activities" landing list. */}
                <Route path="daily-reporting" element={
                  <ProtectedRoute requiredMenuKey="promoter-activities" requiredMenuAction="read">
                    <DailyReporting />
                  </ProtectedRoute>
                } />
                <Route path="voice-agent-test" element={
                  <ProtectedRoute>
                    <VoiceAgent />
                  </ProtectedRoute>
                } />
                {/* Old /promoter-activities URLs redirect to the new workspace.
                    The :id detail/edit pages still work for legacy activity
                    records created under the previous schema. */}
                <Route path="promoter-activities" element={<Navigate to="/daily-reporting" replace />} />
                <Route path="promoter-activities/new" element={
                  <ProtectedRoute requiredMenuKey="promoter-activities" requiredMenuAction="create">
                    <PromoterActivityForm />
                  </ProtectedRoute>
                } />
                <Route path="promoter-activities/:id" element={
                  <ProtectedRoute requiredMenuKey="promoter-activities" requiredMenuAction="read">
                    <PromoterActivityDetails />
                  </ProtectedRoute>
                } />
                <Route path="promoter-activities/:id/edit" element={
                  <ProtectedRoute requiredMenuKey="promoter-activities" requiredMenuAction="update">
                    <PromoterActivityForm />
                  </ProtectedRoute>
                } />
                            {/* Other Routes */}
                <Route path="operations" element={
                  <ProtectedRoute requiredMenuKey="operations" requiredMenuAction="read">
                    <Operations />
                  </ProtectedRoute>
                } />
            
                {/* Warehouse Inventory Management */}
                <Route path="warehouse-inventory" element={
                  <ProtectedRoute requiredMenuKey="warehouse-inventory" requiredMenuAction="view">
                    <WarehouseInventory />
                  </ProtectedRoute>
                } />
                <Route path="campaign-inventory-manager" element={
                  <ProtectedRoute requiredMenuKey="campaign-inventory" requiredMenuAction="read">
                    <CampaignInventoryManager />
                  </ProtectedRoute>
                } />
                {/* Test route without permissions */}
                <Route path="warehouse-inventory-test" element={
                  <WarehouseInventoryTest />
                } />
                <Route path="godown-items-test/:id" element={
                  <GodownItemCreate />
                } />
                <Route path="campaigns/:id/inventory" element={
                  <ProtectedRoute requiredMenuKey="campaign-inventory" requiredMenuAction="read">
                    <CampaignInventory />
                  </ProtectedRoute>
                } />
                <Route path="godown-items/create" element={
                  <ProtectedRoute requiredMenuKey="warehouse-inventory" requiredMenuAction="create">
                    <GodownItemCreate />
                  </ProtectedRoute>
                } />
                <Route path="godown-items/:id" element={
                  <ProtectedRoute requiredMenuKey="warehouse-inventory" requiredMenuAction="read">
                    <GodownItemCreate />
                  </ProtectedRoute>
                } />
                <Route path="godown-items/:id/edit" element={
                  <ProtectedRoute requiredMenuKey="warehouse-inventory" requiredMenuAction="update">
                    <GodownItemCreate />
                  </ProtectedRoute>
                } />
            
                <Route path="expenses" element={
                  <ProtectedRoute requiredMenuKey="expenses" requiredMenuAction="read">
                    <Expenses />
                  </ProtectedRoute>
                } />
                {/* Overhead Expenses was merged into Overheads (Tally) — one page, one figure. */}
                <Route path="overhead-expenses" element={<Navigate to="/accounts/overheads" replace />} />
                <Route path="expenses/new" element={
                  <ProtectedRoute requiredMenuKey="expenses" requiredMenuAction="create">
                    <ExpenseCreateChooser />
                  </ProtectedRoute>
                } />
                <Route path="expenses/new/field" element={
                  <ProtectedRoute requiredMenuKey="expenses" requiredMenuAction="create">
                    <ExpenseCreate />
                  </ProtectedRoute>
                } />
                <Route path="expenses/new/:type" element={
                  <ProtectedRoute requiredMenuKey="expenses" requiredMenuAction="create">
                    <ExpenseCreateAccounts />
                  </ProtectedRoute>
                } />
                <Route path="expenses/bulk-upload" element={
                  <ProtectedRoute requiredMenuKey="expenses" requiredMenuAction="create">
                    <ExpensesBulkUpload />
                  </ProtectedRoute>
                } />
                <Route path="expenses/bulk-pay" element={
                  <ProtectedRoute requiredMenuKey="payments" requiredMenuAction="create">
                    <BulkVendorPayment />
                  </ProtectedRoute>
                } />
                <Route path="expenses/reconciliation" element={
                  <ProtectedRoute requiredMenuKey="expenses" requiredMenuAction="read">
                    <ExpenseReconciliation />
                  </ProtectedRoute>
                } />
                <Route path="expenses/:id" element={
                  <ProtectedRoute requiredMenuKey="expenses" requiredMenuAction="read">
                    <ExpenseDetails />
                  </ProtectedRoute>
                } />
                <Route path="expenses/:id/edit" element={
                  <ProtectedRoute requiredMenuKey="expenses" requiredMenuAction="update">
                    <ExpenseCreate />
                  </ProtectedRoute>
                } />
                <Route path="campaign-reports" element={
                  <ProtectedRoute requiredMenuKey="reports" requiredMenuAction="read">
                    <CampaignReportDashboard />
                  </ProtectedRoute>
                } />
                <Route path="reports" element={
                  <ProtectedRoute requiredMenuKey="reports" requiredMenuAction="read">
                    <Reports />
                  </ProtectedRoute>
                } />
                <Route path="report-templates" element={
                  <ProtectedRoute requiredMenuKeys={["reports", "campaigns"]} requiredMenuAction="read">
                    <ReportTemplates />
                  </ProtectedRoute>
                } />
                <Route path="reports/new" element={
                  <ProtectedRoute requiredMenuKey="reports" requiredMenuAction="create">
                    <ReportCreate />
                  </ProtectedRoute>
                } />
                <Route path="reports/:id" element={
                  <ProtectedRoute requiredMenuKey="reports" requiredMenuAction="read">
                    <ReportDetails />
                  </ProtectedRoute>
                } />
                <Route path="reports/:id/edit" element={
                  <ProtectedRoute requiredMenuKey="reports" requiredMenuAction="update">
                    <ReportCreate />
                  </ProtectedRoute>
                } />
                <Route path="accounts" element={
                  <ProtectedRoute requiredMenuKey="finance" requiredMenuAction="read">
                    <Accounts />
                  </ProtectedRoute>
                } />
                <Route path="accounts/payment-requests" element={
                  <ProtectedRoute requiredMenuKeys={['finance', 'payment-requests']} requiredMenuAction="read">
                    <PaymentRequests />
                  </ProtectedRoute>
                } />
                <Route path="accounts/advances" element={
                  <ProtectedRoute requiredMenuKey="finance" requiredMenuAction="read">
                    <AdvanceReconciliation />
                  </ProtectedRoute>
                } />
                <Route path="accounts/payment-allocations" element={
                  <ProtectedRoute requiredMenuKey="finance" requiredMenuAction="read">
                    <PaymentAllocations />
                  </ProtectedRoute>
                } />
                <Route path="accounts/overheads" element={
                  <ProtectedRoute requiredMenuKey="finance" requiredMenuAction="read">
                    <Overheads />
                  </ProtectedRoute>
                } />
                <Route path="accounts/payments-tracker" element={
                  <ProtectedRoute requiredMenuKey="finance" requiredMenuAction="read">
                    <PaymentsTracker />
                  </ProtectedRoute>
                } />
                {/* Accounts entry point for the billing-correction queue — Finance-gated
                    so Accounts (no order.read) can see and action requests. */}
                <Route path="accounts/billing-corrections" element={
                  <ProtectedRoute requiredMenuKey="finance" requiredMenuAction="read">
                    <AccountsBillingCorrectionsPage />
                  </ProtectedRoute>
                } />
                <Route path="analytics" element={
                  <ProtectedRoute requiredMenuKey="analytics" requiredMenuAction="read">
                    <Analytics />
                  </ProtectedRoute>
                } />
            
                <Route path="settings" element={
                  <ProtectedRoute requiredMenuKey="settings" requiredMenuAction="read">
                    <Settings />
                  </ProtectedRoute>
                } />
                <Route path="settings/user-management" element={
                  <ProtectedRoute requiredMenuKey="settings" requiredMenuAction="read">
                    <UserManagement />
                  </ProtectedRoute>
                } />
                <Route path="settings/user-registration" element={
                  <ProtectedRoute requiredMenuKey="user-registration" requiredMenuAction="create">
                    <UserRegistration />
                  </ProtectedRoute>
                } />
                <Route path="settings/roles" element={
                  <ProtectedRoute requiredMenuKey="settings" requiredMenuAction="update">
                    <RolesPermissions />
                  </ProtectedRoute>
                } />
                <Route path="settings/master-data" element={
                  <ProtectedRoute requiredMenuKey="settings" requiredMenuAction="read">
                    <MasterData />
                  </ProtectedRoute>
                } />
                <Route path="settings/master-data/:type" element={
                  <ProtectedRoute requiredMenuKey="settings" requiredMenuAction="update">
                    <MasterDataManagement />
                  </ProtectedRoute>
                } />
                <Route path="settings/system" element={
                  <ProtectedRoute requiredMenuKey="settings" requiredMenuAction="update">
                    <SystemConfiguration />
                  </ProtectedRoute>
                } />
                <Route path="settings/integrations" element={
                  <ProtectedRoute requiredMenuKey="settings" requiredMenuAction="update">
                    <Integrations />
                  </ProtectedRoute>
                } />
            
                {/* Godown Inventory Module */}
                <Route path="godown-inventory" element={
                  <ProtectedRoute requiredMenuKey="godown-inventory" requiredMenuAction="read">
                    <Godowns />
                  </ProtectedRoute>
                } />
                <Route path="godown-inventory/:godownId" element={
                  <ProtectedRoute requiredMenuKey="godown-inventory" requiredMenuAction="read">
                    <GodownInventory />
                  </ProtectedRoute>
                } />
                <Route path="damaged-items" element={
                  <ProtectedRoute requiredMenuKey="damaged-items" requiredMenuAction="view">
                    <DamagedItems />
                  </ProtectedRoute>
                } />
                <Route path="driver-approval" element={
                  <ProtectedRoute requiredMenuKey="driver-approval" requiredMenuAction="read">
                    <DriverApproval />
                  </ProtectedRoute>
                } />
                <Route path="campaign-members/:campaignId" element={
                  <ProtectedRoute requiredMenuKey="campaigns" requiredMenuAction="read">
                    <CampaignMembers />
                  </ProtectedRoute>
                } />

                {/* ── Purchase Module ─────────────────────────────────────── */}
                <Route path="purchase" element={<Navigate to="/purchase/requests" replace />} />
                <Route path="purchase/requests" element={
                  <ProtectedRoute requiredMenuKey="purchase-requests" requiredMenuAction="read">
                    <PurchaseRequests />
                  </ProtectedRoute>
                } />
                <Route path="purchase/requests/new" element={
                  <ProtectedRoute requiredPermission="purchase_request.create">
                    <PurchaseRequestForm />
                  </ProtectedRoute>
                } />
                <Route path="purchase/requests/:id" element={
                  <ProtectedRoute requiredMenuKey="purchase-requests" requiredMenuAction="read">
                    <PurchaseRequestDetails />
                  </ProtectedRoute>
                } />
                <Route path="purchase/requests/:id/edit" element={
                  <ProtectedRoute requiredMenuKey="purchase-requests" requiredMenuAction="update">
                    <PurchaseRequestForm />
                  </ProtectedRoute>
                } />
                <Route path="purchase/rfqs" element={
                  <ProtectedRoute requiredMenuKey="purchase-rfqs" requiredMenuAction="read">
                    <RFQs />
                  </ProtectedRoute>
                } />
                <Route path="purchase/rfqs/new" element={
                  <ProtectedRoute requiredPermission="rfq.create">
                    <RFQDetails />
                  </ProtectedRoute>
                } />
                <Route path="purchase/rfqs/:id" element={
                  <ProtectedRoute requiredMenuKey="purchase-rfqs" requiredMenuAction="read">
                    <RFQDetails />
                  </ProtectedRoute>
                } />
                <Route path="purchase/orders" element={
                  <ProtectedRoute requiredMenuKey="purchase-orders" requiredMenuAction="read">
                    <PurchaseOrders />
                  </ProtectedRoute>
                } />
                <Route path="purchase/po-payments" element={
                  <ProtectedRoute requiredMenuKey="purchase-orders" requiredMenuAction="read">
                    <POPaymentRequests />
                  </ProtectedRoute>
                } />
                <Route path="ofm" element={
                  <FinanceControlRoute>
                    <OFMRequests />
                  </FinanceControlRoute>
                } />
                <Route path="ofm/:id" element={
                  <FinanceControlRoute>
                    <OFMRequestDetails />
                  </FinanceControlRoute>
                } />
                <Route path="purchase/orders/new" element={
                  <ProtectedRoute requiredPermission="purchase_order.create">
                    <PurchaseOrderDetails />
                  </ProtectedRoute>
                } />
                <Route path="purchase/orders/:id" element={
                  <ProtectedRoute requiredMenuKey="purchase-orders" requiredMenuAction="read">
                    <PurchaseOrderDetails />
                  </ProtectedRoute>
                } />
                <Route path="purchase/expense-approvals" element={
                  <ProtectedRoute requiredMenuKey="purchase-dashboard" requiredMenuAction="read">
                    <ExpenseApprovals />
                  </ProtectedRoute>
                } />

                {/* ── Tasks (Flowlu-style 5 views) ───────────────────────── */}
                <Route path="tasks" element={
                  <ProtectedRoute requiredMenuKey="tasks" requiredMenuAction="read">
                    <Tasks />
                  </ProtectedRoute>
                } />
                {/* Chat is available to every authenticated user — no menu gate. */}
                <Route path="chat" element={<ChatPage />} />
                {/* Meetings + calendar — available to every authenticated user. */}
                <Route path="meetings" element={<Meetings />} />
                <Route path="tasks/:id" element={
                  <ProtectedRoute requiredMenuKey="tasks" requiredMenuAction="read">
                    <TaskDetails />
                  </ProtectedRoute>
                } />

                {/* ── Orders (FL000XXX cost-centres / converted leads) ── */}
                <Route path="orders" element={
                  <ProtectedRoute requiredMenuKey="crm" requiredMenuAction="read">
                    <Orders />
                  </ProtectedRoute>
                } />
                <Route path="client-pos" element={
                  <ProtectedRoute requiredMenuKey="crm" requiredMenuAction="read">
                    <ClientPOs />
                  </ProtectedRoute>
                } />
                <Route path="orders/approvals" element={
                  <ProtectedRoute requiredMenuKey="crm" requiredMenuAction="read">
                    <OrderApprovals />
                  </ProtectedRoute>
                } />
                <Route path="orders/reconciliation" element={
                  <ProtectedRoute requiredMenuKey="crm" requiredMenuAction="read">
                    <OrderReconciliation />
                  </ProtectedRoute>
                } />
                {/* Estimate-template approval inbox (reporting-manager "Plus 1" queue). */}
                <Route path="crm/estimate-templates/approvals" element={
                  <ProtectedRoute requiredMenuKey="crm" requiredMenuAction="read">
                    <EstimateTemplateApprovals />
                  </ProtectedRoute>
                } />
                {/* Consolidated CRM document-invoice list (like the Orders/FL list). */}
                <Route path="crm/invoices" element={
                  <ProtectedRoute requiredMenuKey="crm" requiredMenuAction="read">
                    <CRMInvoices />
                  </ProtectedRoute>
                } />

                {/* ── Invoices — Tally Sales/Purchase hub + import ── */}
                <Route path="invoices" element={
                  <ProtectedRoute requiredMenuKey="finance" requiredMenuAction="read">
                    <Invoices />
                  </ProtectedRoute>
                } />

                {/* ── FL P&L + Retainer reports ─ Finance domain ─ */}
                <Route path="reports/fl-pnl" element={
                  <ProtectedRoute requiredMenuKey="finance" requiredMenuAction="read">
                    <FLPnL />
                  </ProtectedRoute>
                } />
                <Route path="reports/fl-pnl/:flNumber" element={
                  <ProtectedRoute requiredMenuKey="finance" requiredMenuAction="read">
                    <FLPnL />
                  </ProtectedRoute>
                } />
                <Route path="reports/retainers" element={
                  <ProtectedRoute requiredMenuKey="finance" requiredMenuAction="read">
                    <RetainerDashboard />
                  </ProtectedRoute>
                } />

                {/* Audit log — page-level gate is `is_superadmin` on the
                    user record, enforced inside the component AND by the
                    /audit-log backend endpoint. No menu-key check here. */}
                <Route path="admin/audit-log" element={<AuditLog />} />
                <Route path="admin/holidays" element={<HolidaysAdmin />} />
                <Route path="admin/tally-mappings" element={<TallyVoucherMappings />} />

                {/* ── CRM (Opportunities / Leads kanban — Flowlu mirror) ── */}
                <Route path="crm" element={
                  <ProtectedRoute requiredMenuKey="crm" requiredMenuAction="read">
                    <CRM />
                  </ProtectedRoute>
                } />
                <Route path="crm/leads/:leadId" element={
                  <ProtectedRoute requiredMenuKey="crm" requiredMenuAction="read">
                    <CRMLeadDetail />
                  </ProtectedRoute>
                } />
                <Route path="crm/activities" element={
                  <ProtectedRoute requiredMenuKey="crm" requiredMenuAction="read">
                    <CRMActivities />
                  </ProtectedRoute>
                } />
                <Route path="crm/accounts" element={
                  <ProtectedRoute requiredMenuKey="crm" requiredMenuAction="read">
                    <CRMAccounts />
                  </ProtectedRoute>
                } />
                <Route path="crm/targets" element={
                  <ProtectedRoute requiredMenuKey="crm" requiredMenuAction="read">
                    <TargetAccounts />
                  </ProtectedRoute>
                } />
                <Route path="crm/automation" element={
                  <ProtectedRoute requiredMenuKey="crm" requiredMenuAction="read">
                    <CRMAutomation />
                  </ProtectedRoute>
                } />
                <Route path="crm/archived" element={
                  <ProtectedRoute requiredMenuKey="crm" requiredMenuAction="read">
                    <CRMArchived />
                  </ProtectedRoute>
                } />

                {/* ── CRM analytics ─ pipeline + revenue KPIs that belong with the deal flow ── */}
                {/* Campaign P&L states profit and margin per project. That is a
                    finance figure — delivery owners read budget utilisation on the
                    project page instead. */}
                <Route path="analytics/profitability" element={
                  <ProtectedRoute requiredMenuKey="finance" requiredMenuAction="read">
                    <AnalyticsProfitability />
                  </ProtectedRoute>
                } />
                <Route path="analytics/crm-pipeline" element={
                  <ProtectedRoute requiredMenuKey="crm" requiredMenuAction="read">
                    <AnalyticsPipeline />
                  </ProtectedRoute>
                } />

                {/* My Account — profile + support tickets + feature requests */}
                <Route path="my-account" element={<MyAccount />} />
                <Route path="change-password" element={<ChangePassword />} />

                {/* Employee directory + xlsx import — admin/HR only */}
                <Route path="settings/employees" element={
                  <ProtectedRoute requiredMenuKey="employees" requiredMenuAction="read">
                    <EmployeeDirectory />
                  </ProtectedRoute>
                } />
                <Route path="settings/employees/:id" element={
                  <ProtectedRoute requiredMenuKey="employees" requiredMenuAction="read">
                    <EmployeeDetail />
                  </ProtectedRoute>
                } />
                <Route path="settings/employee-import" element={
                  <ProtectedRoute requiredMenuKey="employee-import" requiredMenuAction="create">
                    <EmployeeImport />
                  </ProtectedRoute>
                } />

                {/* Support / Feature requests — list/detail */}
                <Route path="support/dashboard" element={
                  <ProtectedRoute requiredMenuKey="support" requiredMenuAction="read">
                    <SupportDashboard />
                  </ProtectedRoute>
                } />
                <Route path="support/tickets/:id" element={<SupportTicketDetail />} />
                <Route path="support/features/:id" element={<SupportFeatureDetail />} />

                {/* Role-based focused dashboards */}
                <Route path="operations-dashboard" element={
                  <ProtectedRoute requiredMenuKey="operations-dashboard" requiredMenuAction="read">
                    <OperationsManagerDashboard />
                  </ProtectedRoute>
                } />
                <Route path="purchase-dashboard" element={
                  <ProtectedRoute requiredMenuKey="purchase-dashboard" requiredMenuAction="read">
                    <PurchaseDashboard />
                  </ProtectedRoute>
                } />
                <Route path="creative-dashboard" element={
                  <ProtectedRoute requiredMenuKeys={["dashboard", "tasks"]} requiredMenuAction="read">
                    <CreativeDashboard />
                  </ProtectedRoute>
                } />

                {/* Knowledge Base / Help manual — available to every logged-in user. */}
                <Route path="knowledge-base" element={<KnowledgeBase />} />
                <Route path="knowledge-base/:id" element={<KnowledgeBase />} />
                <Route path="help" element={<HelpManual />} />

                {/* BD persona dashboards — gated by /personas/me. Each path
                    only renders when the resolved persona key matches; any
                    other user lands on /403. */}
                <Route path="personas/bd-executive" element={
                  <PersonaRoute persona="bd_executive">
                    <BDExecutiveDashboard />
                  </PersonaRoute>
                } />
                <Route path="personas/cs-btl" element={
                  <PersonaRoute persona="cs_btl">
                    <BTLClientServicingDashboard />
                  </PersonaRoute>
                } />
                <Route path="personas/cs-digital" element={
                  <PersonaRoute persona="cs_digital">
                    <DigitalClientServicingDashboard />
                  </PersonaRoute>
                } />
                <Route path="personas/bd-pure" element={
                  <PersonaRoute persona="bd_pure">
                    <PureBDDashboard />
                  </PersonaRoute>
                } />
                <Route path="personas/bd-leadgen" element={
                  <PersonaRoute persona="bd_leadgen">
                    <LeadGenDashboard />
                  </PersonaRoute>
                } />

                {/* Welcome page - fallback for roles without a dedicated dashboard */}
                <Route path="welcome" element={<WelcomePage />} />
              </Route>
            </Routes>
          </Suspense>
          </JourneyProvider>
        </BrowserRouter>
        </PersonaProvider>
      </PermissionsProvider>
      <Toaster position="top-right" />
      <HotToaster position="top-right" />
    </QueryClientProvider>
  );
}

export default App;
